'use client';
import {
  Banner,
  FixedBottomNavbar,
  Footer,
  HeaderBottom,
  HeaderTop,
} from '@/components';
import {
  useMenuItemListQuery,
  useSiteSettingsQuery,
  useUserComparisonListQuery,
  useUserFavoritesQuery,
  useUserViewedVariantsQuery,
} from '@/graphql';
import useHasMounted from '@/hooks/useHasMounted';
import { useLanguageCode } from '@/hooks/useLanguageCode';
import { usePathname } from '@/i18n/navigation';
import useYandexMetrika from '@/lib/useYandexMetrika';
import comparisonStore from '@/zustand/comparison';
import { favoritesStore } from '@/zustand/favourite';
import useUserStore from '@/zustand/useUser';
import viewedVariantStore from '@/zustand/viewedVariant';
import { GoogleTagManager, GoogleAnalytics } from '@next/third-parties/google';
import Cookies from 'js-cookie';
import React, { useEffect } from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './providers.module.scss';
import useFacebookPixel from '@/lib/useFacebookPixel';
import useGoogleAnalytics from '@/lib/useGoogleAnalytics';
import useJivoChat from '@/lib/useJivoChat';

const Providers = ({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) => {
  const pathname = usePathname();
  const { user } = useUserStore();
  const languageCode = useLanguageCode();

  const { data: siteSettings } = useSiteSettingsQuery({
    context: {
      requiresAuth: false,
    },
  });

  useYandexMetrika(siteSettings?.shop?.yandexMetrikaCode || '100198313');
  useFacebookPixel(siteSettings?.shop?.facebookPixelCode || '100198313');
  // useClientGoogleAds(siteSettings?.shop?.googleAdsId || '100198313');
  useGoogleAnalytics(siteSettings?.shop?.googleAnalyticsCode || '');
  useJivoChat(siteSettings?.shop?.jivoChatCode || '');

  const setFavorites = favoritesStore(e => e.setFavorites);
  const setComparison = comparisonStore(e => e.setComparison);
  const setViewedVariant = viewedVariantStore(e => e.setViewedVariant);

  const { data: favorites } = useUserFavoritesQuery({
    variables: {
      first: 100,
    },
    context: {
      requiresAuth: true,
    },
    skip: !user,
  });

  const { data: comparison } = useUserComparisonListQuery({
    context: {
      requiresAuth: true,
    },
    skip: !user,
  });

  const { data: viewedVariants } = useUserViewedVariantsQuery({
    variables: {
      first: 100,
    },
    context: {
      requiresAuth: true,
    },
    skip: !user,
  });

  useEffect(() => {
    if (favorites?.me?.favorites?.edges) {
      setFavorites(favorites?.me?.favorites?.edges || []);
    }
    if (comparison?.me?.comparison) {
      setComparison(comparison.me?.comparison || []);
    }
    if (viewedVariants?.me?.viewedVariants?.edges) {
      setViewedVariant(viewedVariants?.me?.viewedVariants?.edges || []);
    }
  }, [comparison, favorites, viewedVariants]);

  useEffect(() => {
    const token = Cookies.get('accessToken');
    const refreshToken = Cookies.get('refreshToken');

    if (!token && refreshToken) {
      fetch('/api/token', {
        method: 'GET',
        credentials: 'include',
      })
        .then(res => res.json())
        .then(data => {
          if (data.token) {
            Cookies.set('accessToken', data.token);
          }
        })
        .catch(err => {
          console.error('Token refresh failed:', err);
        });
    }
  }, []);

  const { data: menuList, loading } = useMenuItemListQuery({
    variables: {
      first: 100,
      languageCode: languageCode,
    },
  });

  const footerMenu = menuList?.menus?.edges.find(e => e.node.name === 'Footer');
  const headerMenu = menuList?.menus?.edges.find(e => e.node.name === 'Header');

  const hasMounted = useHasMounted();
  return (
    // <CustomProvider locale={ru}>
    <div>
      {hasMounted && (
        <GoogleTagManager
          gtmId={process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER ?? ''}
        />
      )}
      <GoogleAnalytics gaId={siteSettings?.shop?.googleAnalyticsCode ?? ''} />

      <div className={styles.providers}>
        {pathname === '/catalog/category/smartphones' && <Banner />}
        <HeaderTop menu={headerMenu?.node} loading={loading} />
        <HeaderBottom />

        {children}
        <Footer menu={footerMenu?.node} loading={loading} />
        <div className={styles.fixedNavbar}>
          <FixedBottomNavbar />
        </div>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar
        closeOnClick
        pauseOnHover
        draggable
      />
    </div>
    // </CustomProvider>
  );
};

export default Providers;
